import 'mapbox-gl/dist/mapbox-gl.css';

import Box from '@material-ui/core/Box';
import RootRef from '@material-ui/core/RootRef';
import { MuiThemeProvider, makeStyles, useTheme } from '@material-ui/core/styles';
import { midpoint, point as toPoint } from '@turf/turf';
import uniqBy from 'lodash.uniqby';
import mapboxgl, {
  MapLayerMouseEvent,
  MapMouseEvent,
  MapboxGeoJSONFeature,
  PointLike,
  Style
} from 'mapbox-gl';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import BaseMapControl from '../controls/BaseMapControl';
import DrawControl from '../controls/DrawControl';
import { LayerBounds } from '../models/Layer';

const { REACT_APP_MAPBOX_ACCESS_TOKEN: MAPBOX_ACCESS_TOKEN } = process.env;

export interface Props {
  baseMapStyles: string[];
  onSwitchBaseMap: (id: string) => void;
  center: [number, number];
  triggerMapResize: string;
  mapStyle: Style | string;
  zoom: number;
  fitBounds: [number, number, number, number] | null;
  popupComponent: React.ReactElement;
  isPopupLoading: boolean;

  /** Array of layer ids for map layers to identify on click */
  identifyLayers?: string[];

  /** Handler for map click and identify features from `identifyLayers` */
  onIdentify: (identify: {
    location: { lng: number; lat: number };
    features: Array<{ id: string; [key: string]: any }>;
  }) => void;

  onAgreementBounds: (agreementBounds: LayerBounds) => void;
}

const Map: React.FC<Props> = ({
  baseMapStyles,
  onSwitchBaseMap,
  center,
  triggerMapResize,
  zoom,
  fitBounds,
  mapStyle,
  identifyLayers = [],
  popupComponent,
  onIdentify,
  onAgreementBounds,
  isPopupLoading
}) => {
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const mapContainer = useRef(null);

  // Track if the initial map location has been set.
  // If it has not, we don't fly to when setting location.
  const initialLocationSet = useRef(false);

  const classes = useStyles();

  const theme = useTheme(); // Get theme to apply to control theme providers

  /** Initialize mapbox-gl map on component mount */
  useEffect(() => {
    if (!mapContainer.current) {
      throw new Error('Ref to map container element must be assigned before rendering');
    }

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN as string;

    const m = new mapboxgl.Map({
      container: (mapContainer.current as unknown) as Element,
      maxZoom: 18,
      customAttribution: 'LJA Engineering, Inc.'
    });

    // Disable default zoom behavior
    m.boxZoom.disable();

    setMap(m);

    return () => {
      m.remove();
      setMap(null);
    };
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }

    const controls = [
      new mapboxgl.NavigationControl(),
      new BaseMapControl(baseMapStyles, onSwitchBaseMap),
      new DrawControl()
    ];
    controls.forEach(control => map.addControl(control));

    return () => controls.forEach(control => map.removeControl(control));
  }, [map, baseMapStyles, onSwitchBaseMap]);

  /** Query for agreements when the map is ready to call `onAgreementBounds` */
  useEffect(() => {
    if (!map || !onAgreementBounds) {
      return;
    }

    map.on('load', () => {
      const lines: any[] = map.querySourceFeatures('tctrr', { sourceLayer: 'properties_line' });
      const points: any[] = map.querySourceFeatures('tctrr', {
        sourceLayer: 'properties_point'
      });
      const polys: any[] = map.querySourceFeatures('tctrr', { sourceLayer: 'properties_poly' });

      const uniqByFileNumber = [...lines, ...points, ...polys].reduce((acc, agreement) => {
        const bounds = JSON.parse(agreement.properties._bounds);
        const popupAnchor = JSON.parse(agreement.properties._popupAnchor);

        if (acc[agreement.properties['File Number']]) {
          const existingBounds = acc[agreement.properties['File Number']].bounds;
          existingBounds[0] = Math.min(existingBounds[0], bounds[0]);
          existingBounds[1] = Math.min(existingBounds[1], bounds[1]);
          existingBounds[2] = Math.max(existingBounds[2], bounds[2]);
          existingBounds[3] = Math.max(existingBounds[3], bounds[3]);
          return acc;
        }

        acc[agreement.properties['File Number']] = {
          popupAnchor,
          bounds
        };

        return acc;
      }, {});

      onAgreementBounds(uniqByFileNumber);
    });
  }, [map, onAgreementBounds]);

  /** Update map style on `mapStyle` change */
  useEffect(() => {
    map && map.setStyle(mapStyle);
  }, [map, mapStyle]);

  /** Fly to `center` and `zoom` on change */
  useEffect(() => {
    if (!map) {
      return;
    }

    // If initial location has not been set, this is the first load of the map.
    // Jump straight to location without animation.
    // Otherwise, this is a location change to an existing, loaded map. Fly to loc with animation.
    if (!initialLocationSet.current) {
      map.jumpTo({ center, zoom });
      initialLocationSet.current = true;
    } else {
      map.flyTo({ center, zoom });
    }
  }, [map, center, zoom]);

  useEffect(() => {
    if (!map || !fitBounds) {
      return;
    }

    // Use percentage of map height for top offset to allow room for popup
    const mapHeight = map.getContainer().offsetHeight;
    map.fitBounds(fitBounds, {
      maxZoom: 16,
      padding: { top: mapHeight * 0.64, bottom: 20, left: 80, right: 80 }
    });
  }, [fitBounds, map]);

  /** Resize map canvas on incoming triggers */
  useEffect(() => {
    if (!map) {
      return;
    }

    map.resize();
  }, [triggerMapResize, map]);

  /** Identify features on map click and call `onIdentify` with features */
  useEffect(() => {
    if (!map || !identifyLayers.length) {
      return;
    }

    map.on('click', onClick);

    identifyLayers.forEach(layer => {
      map.on('mouseenter', layer, updateCursor);
      map.on('mouseleave', layer, resetCursor);
    });

    return () => {
      map.off('click', onClick);

      identifyLayers.forEach(layer => {
        map.off('mouseenter', layer, updateCursor);
        map.off('mouseleave', layer, resetCursor);
      });
    };

    function onClick({ lngLat, point, target }: MapLayerMouseEvent) {
      const features = identifyFeatures(target, point, identifyLayers);
      onIdentify({ location: lngLat, features });
    }

    function updateCursor({ target }: MapMouseEvent) {
      target.getCanvas().style.cursor = 'pointer';
    }

    function resetCursor({ target }: MapMouseEvent) {
      target.getCanvas().style.cursor = '';
    }
  }, [map, identifyLayers, onIdentify]);

  /** Identify features on map shift + drag and call `onIdentify` with features */
  useEffect(() => {
    if (!map || !identifyLayers.length) {
      return;
    }

    let start: mapboxgl.Point;
    let current: mapboxgl.Point;
    let box: any;

    const canvas = map.getCanvasContainer();

    canvas.addEventListener('mousedown', onMouseDown, true);

    return () => {
      map.dragPan.enable();

      if (box) {
        box.parentNode.removeChild(box);
      }

      canvas.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    function onMouseDown(event) {
      if (!(event.shiftKey && event.button === 0)) {
        // Shift key was not pressed
        return;
      }

      map!.dragPan.disable();

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);

      current = start = getMousePosition(event);
    }

    function onMouseMove(event) {
      current = getMousePosition(event);

      if (!box) {
        // Draw drag box on the map canvas
        box = document.createElement('div');
        box.style.background = 'rgba(56, 135, 190, 0.1)';
        box.style.border = '2px solid #3887be';
        canvas.appendChild(box);
      }

      const minX = Math.min(start.x, current.x);
      const maxX = Math.max(start.x, current.x);
      const minY = Math.min(start.y, current.y);
      const maxY = Math.max(start.y, current.y);

      const pos = 'translate(' + minX + 'px,' + minY + 'px)';

      // Set new box geometry
      box.style.transform = pos;
      box.style.WebkitTransform = pos;
      box.style.width = maxX - minX + 'px';
      box.style.height = maxY - minY + 'px';
    }

    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      if (box) {
        box.parentNode.removeChild(box);
        box = null;
      }

      const features = identifyFeatures(map!, [start, current], identifyLayers);

      // Convert screen position -> Lng/Lat
      const startLngLat = map!.unproject(start);
      const currentLngLat = map!.unproject(current);

      // Convert mapbox.LngLat instance -> GeoJSON Feature<Point> for midpoint calc
      const startPoint = toPoint([startLngLat.lng, startLngLat.lat]);
      const currentPoint = toPoint([currentLngLat.lng, currentLngLat.lat]);

      const mid = midpoint(startPoint, currentPoint);

      const location = {
        lng: mid.geometry!.coordinates[0],
        lat: mid.geometry!.coordinates[1]
      };

      onIdentify({ location, features });

      map!.dragPan.enable();
    }

    function getMousePosition(event) {
      const rect = canvas.getBoundingClientRect();

      return new mapboxgl.Point(
        event.clientX - rect.left - canvas.clientLeft,
        event.clientY - rect.top - canvas.clientTop
      );
    }
  }, [map, identifyLayers, onIdentify]);

  /**
   * Render popup component.
   * Renders in an actual popup if the map location (`popupLngLat`) is known.
   * If location is not known, renders in map control (bottom left).
   */
  useEffect(() => {
    if (!map || !popupComponent || isPopupLoading) {
      return;
    }

    const container = document.createElement('div');
    ReactDOM.render(<MuiThemeProvider theme={theme}>{popupComponent}</MuiThemeProvider>, container);

    // Originally had logic to render a popup above the feature when
    // we had the coordinates.
    // TCTRR requested we move all popups to the bottom left
    // as it was intefering with the vision underneath the popup
    // TCTR-51
    const positionEl = (map as any)._controlPositions['bottom-left'];
    positionEl.classList.add(classes.popupControlContainer);
    container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    positionEl.appendChild(container);

    return () => {
      positionEl && positionEl.removeChild(container);
      ReactDOM.unmountComponentAtNode(container);
    };
  }, [classes, map, popupComponent, theme, isPopupLoading]);

  return (
    <RootRef rootRef={mapContainer}>
      <Box component="div" flex="1 1 100%" height={1} width={1} />
    </RootRef>
  );
};

export default Map;

/** Find all rendered features in the given array of layers within the given point or box */
const identifyFeatures = (
  map: mapboxgl.Map,
  pointOrBox: PointLike | [PointLike, PointLike],
  layers: string[]
): Array<MapboxGeoJSONFeature & { id: string }> => {
  const clickedFeats = map.queryRenderedFeatures(pointOrBox, {
    layers
  });

  if (!clickedFeats.length) {
    return [];
  }

  // Find only features uniq by layer and id. At tile edges, feats can be duplicated.
  // Create new id property that includes the layer name to ensure uniqueness
  return uniqBy(clickedFeats, (f: any) => f.layer.id + f.properties._id).map((f: any) => {
    return { ...f, id: `${f.layer.id}:${f.properties._id}` };
  });
};

const addImage =
  // tslint:disable-next-line: max-line-length
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwAQMAAABtzGvEAAAABlBMVEUAAAAAAAClZ7nPAAAAAXRSTlMAQObYZgAAABpJREFUeAFjoBZgPkB1yv7//z9YKVrYRyUAAGoSHy2hvqxVAAAAAElFTkSuQmCC';

const fullscreenImage =
  // tslint:disable-next-line: max-line-length
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgAQMAAADYVuV7AAAABlBMVEUAAAAzMzPI8eYgAAAAAXRSTlMAQObYZgAAACZJREFUeAFjAIFRwP//PwPD//8faM4BU/yDjjPMwGAOa3qmt1EAAJi6f4Gal950AAAAAElFTkSuQmCC';

const removeImage =
  // tslint:disable-next-line: max-line-length
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAAKklEQVR4Ae3PAQkAAAzDsPs3/Ys4jAEJVECnHgAAAMCGezFgoBoAAADAAYiWb5Ec1zNuAAAAAElFTkSuQmCC';

const shrinkImage =
  // tslint:disable-next-line: max-line-length
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgAQMAAADYVuV7AAAABlBMVEUAAAAzMzPI8eYgAAAAAXRSTlMAQObYZgAAACNJREFUeAFjgIBR8B+IBieH/z+I8/8DLTnDCtAv3AZ72hkFAK3Sf4Hpvgm3AAAAAElFTkSuQmCC';

const useStyles = makeStyles(theme => ({
  popupControlContainer: {
    // Mapbox control position element (ie. mapboxgl-ctrl-bottom-left) to render popup as a control
    // when a popup cannot be placed as a popup because it does not have a location
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    '& > .mapboxgl-ctrl-group': {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100%',
      maxWidth: 320
    }
  },
  '@global': {
    '.mapboxgl-map': {
      ...theme.typography.body1
    },
    '.mapboxgl-ctrl-bottom-left': {
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        overflowY: 'scroll'
      }
    },
    '.mapboxgl-ctrl-group': {
      overflow: 'hidden',
      borderRadius: theme.shape.borderRadius,
      '& > button:focus': {
        boxShadow: 'none',
        '&:first-child': {
          borderRadius: 0
        },
        '&:last-child': {
          borderRadius: 0
        }
      },
      '&:not(:empty)': {
        boxShadow: theme.shadows['2']
      }
    },
    '.mapboxgl-ctrl': {
      '&.mapboxgl-ctrl-attrib': {
        ...theme.typography.caption,
        lineHeight: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        '&, & a': {
          color: 'rgba(255, 255, 255, 0.75)'
        }
      }
    },
    'a.mapboxgl-ctrl-logo': {
      display: 'none'
    },
    '.mapboxgl-ctrl-icon': {
      backgroundPosition: 'center center',
      backgroundSize: '24px 24px',
      backgroundRepeat: 'no-repeat',
      '&.mapboxgl-ctrl-fullscreen': {
        backgroundImage: `url(${fullscreenImage})`
      },
      '&.mapboxgl-ctrl-shrink': {
        backgroundImage: `url(${shrinkImage})`
      },
      '&.mapboxgl-ctrl-zoom-in': {
        backgroundImage: `url(${addImage})`
      },
      '&.mapboxgl-ctrl-zoom-out': {
        backgroundImage: `url(${removeImage})`
      }
    },
    '.mapboxgl-popup-content': {
      padding: 0,
      paddingBottom: 2,
      boxShadow: theme.shadows['2'],
      borderRadius: theme.shape.borderRadius,
      '.mapboxgl-popup-anchor-top-left &': {
        borderTopLeftRadius: theme.shape.borderRadius
      },
      '.mapboxgl-popup-anchor-top-right &': {
        borderTopRightRadius: theme.shape.borderRadius
      },
      '.mapboxgl-popup-anchor-bottom-left &': {
        borderBottomLeftRadius: theme.shape.borderRadius
      },
      '.mapboxgl-popup-anchor-bottom-right &': {
        borderBottomRightRadius: theme.shape.borderRadius
      },
      '& > div': {
        maxHeight: '35vh',
        display: 'flex',
        flexDirection: 'column',
        '& > *': {
          flex: '1 1 auto'
        },
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          overflowY: 'scroll'
        }
      }
    },
    '.mapboxgl-popup-close-button': {
      display: 'block',
      height: 24,
      width: 24,
      fontSize: '18px',
      borderTopRightRadius: theme.shape.borderRadius,
      '&:focus': {
        outline: 'none'
      }
    }
  }
}));
